import { createHook } from '@atomify/hooks';
import {
    CenterOffset,
    Dimensions,
    InteractionBehavior,
    InteractionState,
    ScreenCoordinates,
} from '@features/virtual-tour/types';
import { imageResize, imageSwap } from '@features/virtual-tour/utilities/interactive-image';

export interface InteractiveImageHookProps {
    imageContainer: { current: HTMLElement | null };
    viewport: { current: HTMLElement | null };
    onResize?: (dimensions: Dimensions) => void;
}

export const usePanningImage = ({
    imageContainer,
    viewport,
    onResize,
}: InteractiveImageHookProps) => {
    const imageMovement: ScreenCoordinates = { x: 0, y: 0 };
    let naturalDimensions: Dimensions;
    let interactionBehavior: InteractionBehavior;

    return createHook({
        onDidLoad: () => {
            return {
                setInteractionBehavior: (behavior: InteractionBehavior) => {
                    if (interactionBehavior === behavior) return;
                    interactionBehavior = behavior;

                    if (!imageContainer.current) return;
                    if (behavior === 'scroll') {
                        imageContainer.current.style.transform = 'none';
                    } else if (behavior === 'track-mouse' && viewport.current) {
                        imageContainer.current.style.transform = 'translate(-50%, -50%)';
                        viewport.current.scrollLeft = 0;
                        viewport.current.scrollTop = 0;
                    }
                },
                setSource: async (source: string) => {
                    if (!imageContainer.current || !viewport.current) return;

                    viewport.current.scrollLeft = 0;
                    viewport.current.scrollTop = 0;
                    const image = await imageSwap(imageContainer.current, source);

                    naturalDimensions = {
                        width: image?.naturalWidth || 0,
                        height: image?.naturalHeight || 0,
                    };

                    return image;
                },
                pan: ({ centerOffset }: InteractionState) => {
                    if (!imageContainer.current) return;

                    const imageTranslate: CenterOffset = {
                        x: centerOffset!.x * imageMovement.x * 100 + 50,
                        y: centerOffset!.y * imageMovement.y * 100 + 50,
                    };

                    imageContainer.current.style.transform = `translate(-${imageTranslate.x}%, -${imageTranslate.y}%)`;
                },
                focusPoint: ({ x, y }: ScreenCoordinates) => {
                    if (interactionBehavior === 'track-mouse' && imageContainer.current) {
                        const pointOffset: CenterOffset = {
                            x: x - 50,
                            y: y - 50,
                        };

                        const imageTranslate: CenterOffset = {
                            x: pointOffset!.x * imageMovement.x + 50,
                            y: pointOffset!.y * imageMovement.y + 50,
                        };
                        imageContainer.current.style.transform = `translate(-${imageTranslate.x}%, -${imageTranslate.y}%)`;
                    } else if (interactionBehavior === 'scroll' && viewport.current) {
                        const imageScroll: ScreenCoordinates = {
                            x: (x / 100) * imageMovement.x * naturalDimensions.width,
                            y: (y / 100) * imageMovement.y * naturalDimensions.height,
                        };

                        viewport.current.scrollTo({
                            left: imageMovement.x > 0.01 ? imageScroll.x : 0,
                            top: imageMovement.y > 0.01 ? imageScroll.y : 0,
                            behavior: 'smooth',
                        });
                    }
                },
                resize: (viewportDimensions: Dimensions) => {
                    if (!imageContainer.current || !naturalDimensions) return;

                    const { width = 0, height = 0 } =
                        imageResize(naturalDimensions, viewportDimensions) || {};

                    naturalDimensions = { width, height };

                    imageContainer.current.style.width = `${width}px`;
                    imageContainer.current.style.height = `${height}px`;

                    imageMovement.x = (width - viewportDimensions.width) / width;
                    imageMovement.y = (height - viewportDimensions.height) / height;

                    if (interactionBehavior === 'scroll' && viewport.current) {
                        const imageScroll: ScreenCoordinates = {
                            x: (imageMovement.x * width) / 2,
                            y: (imageMovement.y * height) / 2,
                        };

                        viewport.current.scrollTo({
                            left: imageMovement.x > 0.01 ? imageScroll.x : 0,
                            top: imageMovement.y > 0.01 ? imageScroll.y : 0,
                            behavior: 'smooth',
                        });
                    }
                    onResize && onResize({ width, height });
                },
                imageMovement,
                naturalDimensions,
            };
        },
    });
};
